import {AIAssetType, AssetType, CreativeAssetsSource} from 'common/src/modules/Assets/Assets.enums';
import getActiveComp from 'common/src/modules/creativeTemplates/copilot/agents/adBuilderAgent/utils/getActiveComp';
import {ToolExecuteParams} from 'common/src/modules/shared/copilot/copilot.types';
import tool from 'common/src/modules/shared/copilot/utils/creators/tool';
import {CLStore} from 'common/src/redux/store';
import {z} from 'zod';
import {AdBuilderAgentToolName} from '../adBuilderAgent.enums';
import mapAICreativeAssetTypeToTemplateAssetType from '../utils/mapAICreativeAssetTypeToTemplateAssetType';

// Schema for getting available assets
const inputSchema = z.object({
  source: z.nativeEnum(CreativeAssetsSource).describe('Source of assets to retrieve'),
  assetType: z
    .nativeEnum(AIAssetType)
    .describe('Type of assets to retrieve. only image/video/font are supported'),
});

function getAvailableAssetsTool(store: CLStore) {
  return tool({
    name: AdBuilderAgentToolName.GetAvailableAssets,
    displayName: 'Getting available assets',
    description:
      'Retrieves assets from either template assets or brand assets, filtered by type. Returns assets with their id and content for agent consumption. ',
    inputSchema,
    execute: async ({input}: ToolExecuteParams<typeof inputSchema>) => {
      const state = store.getState();

      const activeComp = getActiveComp(store);

      if (!activeComp) {
        return {
          content: 'ERROR: No active composition found',
          error: 'No active composition found',
        };
      }

      try {
        // Validate that Font is not requested for BrandAssets
        if (input.source === 'BrandAssets' && input.assetType === AIAssetType.Font) {
          return {
            content:
              'ERROR: Font assets are not available from BrandAssets. Only Image and Video are supported.',
          };
        }

        if (input.source === 'TemplateAssets') {
          // Get template assets from Redux store
          const templateAssets = state.creativeTemplatePage.movieAssets.templateAssets;

          // Filter assets by the requested type and only return active assets
          const targetTemplateAssetType = mapAICreativeAssetTypeToTemplateAssetType(
            input.assetType,
          );
          const filteredAssets = templateAssets.filter(
            (asset) => asset.metadata.type === targetTemplateAssetType,
          );

          // Return essential information for asset selection
          const assetList = filteredAssets.map((asset) => ({
            id: asset.id,
            name: asset.metadata.name || asset.metadata.family || 'Unnamed Asset',
            content: asset.metadata.customMetadata?.ContentDescription || '',
            source: asset.metadata.customMetadata?.Source || '',
          }));

          return {
            content: `Found ${assetList.length} active ${input.assetType.toLowerCase()} template assets: ${JSON.stringify(assetList, null, 2)}`,
          };
        } else {
          // BrandAssets path
          const assetType =
            input.assetType === AIAssetType.Image ? AssetType.Image : AssetType.Video;
          const brandAssets =
            assetType === AssetType.Image
              ? store.getState().brandGuidelinesPage.brandAssets.images
              : store.getState().brandGuidelinesPage.brandAssets.videos;

          // Check if brand assets are loaded
          if (brandAssets === null) {
            return {
              content: `ERROR: ${input.assetType} brand assets are not loaded in the store. Please load them first.`,
            };
          }

          // Map brand assets to match template assets structure
          const assetList = brandAssets.map((asset) => ({
            id: asset.id,
            name: asset.name,
            content: asset.metadata?.general?.title || '',
            source: 'BrandAssets',
          }));

          return {
            content: `Found ${assetList.length} ${input.assetType.toLowerCase()} brand assets: ${JSON.stringify(assetList, null, 2)}`,
          };
        }
      } catch (error) {
        return {
          content: `ERROR: ${error instanceof Error ? error.message : 'Failed to get available assets'}`,
        };
      }
    },
  });
}

export default getAvailableAssetsTool;
